import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { userAPI, itemAPI } from '../services/api';
import { useAuth } from '../contexts/FirebaseAuthContext';
import PinItem from '../components/common/PinItem';
import PromoSlot from '../components/common/PromoSlot';
import LoadingSpinner from '../components/common/LoadingSpinner';
import StarRating from '../components/common/StarRating';
import { MapPin, Calendar, Package, Heart, UserPlus, UserCheck, MessageCircle } from 'lucide-react';

const Profile = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user: currentUser, isAuthenticated } = useAuth();
  const [user, setUser] = useState(null);
  const [userItems, setUserItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isFollowing, setIsFollowing] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchUserProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch user by username
      const userResponse = await userAPI.getUserByUsername(username);
      const userData = userResponse.data.data;
      setUser(userData);
      setFollowersCount(userData.followers?.length || 0);

      // Check if current user is following this profile
      if (isAuthenticated && currentUser && userData.followers) {
        setIsFollowing(userData.followers.includes(currentUser._id));
      }

      // Fetch user's listings
      const itemsResponse = await userAPI.getUserListings(userData._id, {
        page: 1,
        limit: 50,
      });
      setUserItems(itemsResponse.data.data || []);
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError(err.response?.data?.error || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, isAuthenticated, currentUser]);

  useEffect(() => {
    fetchUserProfile();
  }, [fetchUserProfile]);

  const handleItemClick = (item) => {
    navigate(`/items/${item._id}`);
  };

  const handleFollowToggle = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    try {
      if (isFollowing) {
        await userAPI.unfollowUser(user._id);
        setIsFollowing(false);
        setFollowersCount((prev) => prev - 1);
      } else {
        await userAPI.followUser(user._id);
        setIsFollowing(true);
        setFollowersCount((prev) => prev + 1);
      }
    } catch (err) {
      console.error('Error toggling follow:', err);
    }
  };

  const handleMessageUser = () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    navigate(`/messages/${user._id}`);
  };

  const handleLikeItem = async (itemId) => {
    try {
      await itemAPI.likeItem(itemId);
      // Refresh items to update like status
      fetchUserProfile();
    } catch (err) {
      console.error('Error liking item:', err);
    }
  };

  const handleSaveItem = async (itemId) => {
    try {
      await itemAPI.saveItem(itemId);
      // Refresh items to update save status
      fetchUserProfile();
    } catch (err) {
      console.error('Error saving item:', err);
    }
  };

  const isOwnProfile = currentUser && user && currentUser._id === user._id;

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="container" style={{ paddingTop: '40px' }}>
        <div className="card">
          <h2>Error</h2>
          <p className="text-muted">{error}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="container" style={{ paddingTop: '40px' }}>
        <div className="card">
          <h2>User not found</h2>
          <p className="text-muted">The user @{username} does not exist.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingTop: '40px', paddingBottom: '40px' }}>
      {/* User Profile Header */}
      <div className="card" style={{ marginBottom: '32px' }}>
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'center' : 'flex-start',
          textAlign: isMobile ? 'center' : 'left',
          gap: isMobile ? '16px' : '24px',
          marginBottom: '24px',
        }}>
          <img
            src={user.profileImage || `https://ui-avatars.com/api/?name=${user.username}&size=120&background=e60023&color=fff`}
            alt={user.username}
            style={{
              width: isMobile ? '96px' : '120px',
              height: isMobile ? '96px' : '120px',
              borderRadius: '50%',
              objectFit: 'cover',
              flexShrink: 0,
            }}
          />
          <div style={{ flex: 1, width: '100%', minWidth: 0 }}>
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between',
              alignItems: isMobile ? 'center' : 'flex-start',
              gap: isMobile ? '16px' : '12px',
              marginBottom: '12px',
            }}>
              <div style={{ minWidth: 0, maxWidth: '100%' }}>
                <h1 style={{ fontSize: isMobile ? '24px' : '32px', marginBottom: '8px', wordBreak: 'break-word' }}>{user.username}</h1>
                {(user.firstName || user.lastName) && (
                  <p style={{ fontSize: isMobile ? '15px' : '17px', fontWeight: 500, margin: '0 0 4px', wordBreak: 'break-word' }}>
                    {`${user.firstName || ''} ${user.lastName || ''}`.trim()}
                  </p>
                )}
                {user.email && (
                  <p className="text-muted" style={{ fontSize: isMobile ? '14px' : '16px', marginBottom: '12px', wordBreak: 'break-word' }}>
                    {user.email}
                  </p>
                )}
                {user.averageRating > 0 ? (
                  <div style={{ marginBottom: '8px' }}>
                    <StarRating
                      rating={user.averageRating}
                      totalRatings={user.totalRatings}
                      size={18}
                    />
                  </div>
                ) : (
                  <div className="text-muted" style={{ fontSize: '14px', marginBottom: '8px' }}>
                    No ratings yet
                  </div>
                )}
              </div>

              {/* Action Buttons - Only show if not own profile */}
              {!isOwnProfile && isAuthenticated && (
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  width: isMobile ? '100%' : 'auto',
                  flexShrink: 0,
                }}>
                  <button
                    onClick={handleFollowToggle}
                    className={`btn ${isFollowing ? 'btn-outline-primary' : 'btn-primary'}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      padding: '10px 20px',
                      flex: isMobile ? 1 : 'initial',
                    }}
                  >
                    {isFollowing ? (
                      <>
                        <UserCheck size={18} />
                        <span>Following</span>
                      </>
                    ) : (
                      <>
                        <UserPlus size={18} />
                        <span>Follow</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleMessageUser}
                    className="btn btn-outline-primary"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      padding: '10px 20px',
                      flex: isMobile ? 1 : 'initial',
                    }}
                  >
                    <MessageCircle size={18} />
                    <span>Message</span>
                  </button>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', justifyContent: isMobile ? 'center' : 'flex-start' }}>
              {user.location?.city && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--textSecondary)' }}>
                  <MapPin size={16} />
                  <span>{user.location.city}, {user.location.state}</span>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--textSecondary)' }}>
                <Calendar size={16} />
                <span>Joined {new Date(user.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div style={{ display: 'flex', gap: isMobile ? '24px' : '32px', justifyContent: isMobile ? 'space-around' : 'flex-start', paddingTop: '24px', borderTop: '1px solid var(--border)' }}>
          <div>
            <div style={{ fontSize: '24px', fontWeight: '700', marginBottom: '4px' }}>
              {userItems.length}
            </div>
            <div className="text-muted" style={{ fontSize: '14px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Package size={14} />
              <span>Listings</span>
            </div>
          </div>
          <div>
            <div style={{ fontSize: '24px', fontWeight: '700', marginBottom: '4px' }}>
              {followersCount}
            </div>
            <div className="text-muted" style={{ fontSize: '14px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Heart size={14} />
              <span>Followers</span>
            </div>
          </div>
          <div>
            <div style={{ fontSize: '24px', fontWeight: '700', marginBottom: '4px' }}>
              {user.following?.length || 0}
            </div>
            <div className="text-muted" style={{ fontSize: '14px' }}>
              Following
            </div>
          </div>
        </div>
      </div>

      {/* Profile promo slot */}
      <div style={{ marginBottom: '24px' }}>
        <PromoSlot slotId="profile_sidebar" variant="inline" />
      </div>

      {/* User's Listings */}
      <h2 style={{ marginBottom: '24px', fontSize: '24px' }}>Listings</h2>

      {userItems.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '60px 20px' }}>
          <Package size={48} style={{ margin: '0 auto 16px', color: 'var(--textSecondary)' }} />
          <h3 style={{ marginBottom: '8px' }}>No listings yet</h3>
          <p className="text-muted">This user hasn't posted any items for sale.</p>
        </div>
      ) : (
        <div
          style={{
            // Masonry layout matching the home feed (2 columns on mobile,
            // more on wider screens) instead of a single top-to-bottom column.
            columnCount: window.innerWidth > 1200 ? 5 : window.innerWidth > 900 ? 4 : window.innerWidth > 600 ? 3 : 2,
            columnGap: '16px',
          }}
        >
          {userItems.map((item) => (
            <div key={item._id} style={{ breakInside: 'avoid', marginBottom: '16px' }}>
              <PinItem
                item={item}
                onClick={handleItemClick}
                onLike={handleLikeItem}
                onSave={handleSaveItem}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Profile;
